import { ConfigProvider } from 'antd'
import { useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { initGa4IfConfigured } from './analytics/ga4'
import { initMarketingTracking } from './analytics/marketingTracking'
import { LocaleProvider, useUiLocale } from './i18n/LocaleProvider'
import AppLayout from './components/layout/AppLayout'
import BrushLab from './pages/BrushLab'
import ComingSoon from './pages/ComingSoon'
import EditorPage from './pages/EditorPage'
import Home from './pages/Home'
import ItemDetailPage from './pages/ItemDetailPage'
import ItemsPage from './pages/ItemsPage'
import JobsPage from './pages/JobsPage'
import { AuthProvider } from './saas/AuthContext'
import LoginPage from './saas/pages/LoginPage'
import ForgotPasswordPage from './saas/pages/ForgotPasswordPage'
import ResetPasswordPage from './saas/pages/ResetPasswordPage'
import MobileCaptureApp from './saas/pages/MobileCaptureApp'
import MobileCapturePairPage from './saas/pages/MobileCapturePairPage'
import SignUpPage from './saas/pages/SignUpPage'
import VerifyEmailPage from './saas/pages/VerifyEmailPage'
import PlatformApp from './saas/platform/PlatformApp'
import PublicSiteApp from './saas/public/PublicSiteApp'
import WorkspaceApp from './saas/workspace/WorkspaceApp'
import RootEntryRedirect from './saas/RootEntryRedirect'
import SourceEditorPage from './pages/SourceEditorPage'
import PreviewAccessGate from './components/PreviewAccessGate'
import MarketingApp from './marketing/MarketingApp'
import LandingPage from './marketing/LandingPage'
import './marketing/marketing.css'
import { publicLegalRouteElements } from './public/PublicLegalRoutes'
import { resolveFrontendSurface } from './config/surface'

/**
 * `VITE_APP_MODE=admin` is the build used for the Cloud Admin Frontend
 * deployment (Phase 2B-0 spec §21/§33 — reuses this exact project/
 * toolchain rather than a second frontend, just with a different build
 * target and env). Unset/`pipeline` (dev default, and the existing
 * Docker/nginx deployment) keeps today's Pic2Content pipeline UI at "/"
 * completely unchanged. The `/login`, `/platform/*`, `/workspace/*`
 * routes are always registered either way, so local dev/E2E can exercise
 * the SaaS Admin UI without a second build.
 */
const IS_ADMIN_MODE = import.meta.env.VITE_APP_MODE === 'admin'

/** The SaaS App/Signup/Login/Mobile-Capture routes, shared verbatim by
 * both `SaasAppShell` (explicit `VITE_FRONTEND_SURFACE=app` Feature
 * Validation split, or a real Preview/Production App Surface build) and
 * `UnifiedLocalApp` (CURRENT COMPLETE LOCAL, A2 Unified Local Surface)
 * — one Router tree reused by both, never a second copy of these pages. */
function saasRoutes() {
  return (
    <>
      <Route path="/login" element={<LoginPage />} />
      {/* Phase S1 — public self-serve signup + email verification link
          target. Always registered (like /login) regardless of
          VITE_APP_MODE/pipeline build — app.vistoera.com hosts both. */}
      <Route path="/signup" element={<SignUpPage />} />
      <Route path="/verify/:token" element={<VerifyEmailPage />} />
      {/* Password Recovery / Reset — public, unauthenticated, like
          /signup and /verify/:token above. See app/core/email.py's
          build_password_reset_url for the exact URL this route target
          must match ({PUBLIC_ADMIN_BASE_URL}/reset-password/{token}). */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      {/* Mobile Capture — password login / restored session, or QR pair exchange. */}
      <Route path="/mobile-capture" element={<MobileCaptureApp />} />
      <Route path="/mobile-capture/pair/:token" element={<MobileCapturePairPage />} />
      {/* Legacy QR URL shape — redirect handled by treating token as pair path via Navigate. */}
      <Route path="/mobile-capture/:token" element={<MobileCapturePairPage />} />
      {publicLegalRouteElements()}
      <Route path="/platform/*" element={<PlatformApp />} />
      {/* Platform Admin Shell V0.2 — the old `/admin` (VISTOERA Admin
          V0.1 "Control Overview") is retired: its one real page's data
          (workers/tasks/storage) is now folded into the fuller
          `/platform/overview` page below, so a bookmarked `/admin/*`
          link still lands on a real, live page instead of 404ing. */}
      <Route path="/admin/*" element={<Navigate to="/platform/overview" replace />} />
      <Route path="/workspace/*" element={<WorkspaceApp />} />
      {/* Local Public Website (LiangCollection 1:1 Absorption) — no
          RequireAuth wrapper, deliberately: a signed-out visitor must be
          able to open this exact URL, see spec §10/§34.7. */}
      <Route path="/site/:workspaceSlug/:siteSlug/*" element={<PublicSiteApp />} />
    </>
  )
}

/** The SaaS App Surface — everything this file already rendered before
 * Phase SAAS-ENTRY-A1, byte-for-byte unchanged (still gated by
 * `IS_ADMIN_MODE` exactly as before). Extracted into its own component
 * only so `App()` can branch on Surface first — see
 * `resolveFrontendSurface()` (`./config/surface.ts`). Only reached today
 * by a real Preview/Production App Surface build, or a Feature Agent's
 * own explicit `VITE_FRONTEND_SURFACE=app` split-mode dev server — never
 * CURRENT COMPLETE LOCAL, see `UnifiedLocalApp` below. */
function SaasAppShell() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {saasRoutes()}
          {/* VISTOERA_CANVAS_AS_PRIMARY_HOME_AND_GLOBAL_ACCOUNT_ENTRY_V1
              §0/§4 — the App Surface root is no longer the old
              Pic2Content pipeline `Home` page (unauthenticated, predates
              the SaaS product): it now resolves auth state and lands the
              user on Canvas Home (no explicit deep link) or `/login`.
              `Home` itself is untouched and stays reachable at
              `/legacy-pipeline` for any existing bookmark/internal link —
              same retirement pattern as `content-production/:projectId`'s
              old workbench redirect.
              Deliberately registered UNCONDITIONALLY (outside the
              `IS_ADMIN_MODE` branch below): `app-preview.vistoera.com`
              (today's real Preview App Surface host, confirmed live via
              a P0 root-cause fix — the initial version of this route was
              nested inside the `IS_ADMIN_MODE === false` branch only,
              so on the actual admin-mode Preview/Production App Surface
              build `/` fell through to that branch's own
              `<Route path="*" element={<Navigate to="/login" replace />} />`
              instead, bouncing every authenticated user straight back to
              `/login` with zero `/me` calls) IS built with
              `VITE_APP_MODE=admin` — `IS_ADMIN_MODE` is true there, so a
              route nested only inside the `false` branch never registers
              on that build at all. */}
          <Route path="/" element={<RootEntryRedirect />} />
          {IS_ADMIN_MODE ? (
            <Route path="*" element={<Navigate to="/login" replace />} />
          ) : (
            <>
              {/* Standalone brush-interaction experiment — intentionally OUTSIDE
                  AppLayout (no sidebar/nav, no pipeline state) and unlinked from
                  any nav menu; only reachable by typing the URL. See BrushLab.tsx
                  for why this exists. Registered only in dev builds
                  (`import.meta.env.DEV`) so it can never ship to production,
                  while remaining reachable for Playwright, which always runs
                  against the Vite dev server (see playwright.config.ts). */}
              {import.meta.env.DEV && <Route path="/debug/brush-lab" element={<BrushLab />} />}
              <Route element={<AppLayout />}>
                <Route path="/legacy-pipeline" element={<Home />} />
                <Route path="/edit/:imageId" element={<SourceEditorPage />} />
                <Route path="/scenes" element={<ComingSoon title="AI 场景" description="根据透明底图自动生成场景图，暂缓开发" />} />
                <Route path="/content" element={<ComingSoon title="内容编辑" description="图文内容编排，暂缓开发" />} />
                <Route path="/details" element={<ComingSoon title="详情页" description="物品详情长图生成，暂缓开发" />} />
                <Route path="/video" element={<ComingSoon title="视频" description="AI 视频生成，暂缓开发" />} />
                <Route path="/publish" element={<ComingSoon title="发布" description="多平台自动发布，暂缓开发" />} />
                <Route path="/jobs" element={<JobsPage />} />
                {/* Legacy Item/Workspace-based pages — kept mounted but unlinked from nav. */}
                <Route path="/assets" element={<ItemsPage />} />
                <Route path="/assets/:itemId" element={<ItemDetailPage />} />
                <Route path="/editor/:itemId/:assetId" element={<EditorPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </>
          )}
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  )
}

/** CURRENT COMPLETE LOCAL — A2 Unified Local Surface. The default,
 * override-free `vite dev` session (port 5183, see `resolveFrontendSurface`)
 * renders Marketing (`/`) and the SaaS App (login/signup/verify/workspace/
 * platform/mobile-capture) from ONE origin + ONE Router tree, so a user
 * never has to know or open a second port. Reuses `LandingPage` and
 * `saasRoutes()` verbatim — no second copy of any page.
 *
 * The old Pic2Content pipeline UI (`Home`, `ItemsPage`, `JobsPage`, ...)
 * is local-dev-only debris that predates the SaaS product (never reachable
 * on a real Preview/Production App Surface build, see `IS_ADMIN_MODE`
 * above) — kept working, just relocated off `/` to `/legacy-pipeline` so
 * it no longer shadows the Marketing homepage. */
function UnifiedLocalApp() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          {saasRoutes()}
          {import.meta.env.DEV && <Route path="/debug/brush-lab" element={<BrushLab />} />}
          <Route element={<AppLayout />}>
            <Route path="/legacy-pipeline" element={<Home />} />
            <Route path="/edit/:imageId" element={<SourceEditorPage />} />
            <Route path="/scenes" element={<ComingSoon title="AI 场景" description="根据透明底图自动生成场景图，暂缓开发" />} />
            <Route path="/content" element={<ComingSoon title="内容编辑" description="图文内容编排，暂缓开发" />} />
            <Route path="/details" element={<ComingSoon title="详情页" description="物品详情长图生成，暂缓开发" />} />
            <Route path="/video" element={<ComingSoon title="视频" description="AI 视频生成，暂缓开发" />} />
            <Route path="/publish" element={<ComingSoon title="发布" description="多平台自动发布，暂缓开发" />} />
            <Route path="/jobs" element={<JobsPage />} />
            <Route path="/assets" element={<ItemsPage />} />
            <Route path="/assets/:itemId" element={<ItemDetailPage />} />
            <Route path="/editor/:itemId/:assetId" element={<EditorPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  )
}

/**
 * VISTOERA_SAAS_I18N_FOUNDATION V1 — `ConfigProvider`'s `locale` prop must
 * follow the canonical `LocaleProvider` (spec §12/§14), so it has to live
 * INSIDE `<LocaleProvider>`, not alongside it. `App()` below only decides
 * Surface + mounts `<LocaleProvider>`; this component is the one place
 * that reads `useUiLocale()` and feeds it into AntD's `ConfigProvider`.
 */
function LocalizedAppShell({ surface }: { surface: ReturnType<typeof resolveFrontendSurface> }) {
  const { antdLocale } = useUiLocale()

  // VISTOERA_PLATFORM_ADMIN_MARKETING_ANALYTICS_HOME_AND_TRACKING_
  // FOUNDATION_V1 (task spec §12/§96/§97) — the ONE tracking beacon per
  // real page load, fired regardless of Surface. Server-side filtering
  // (`marketing_tracking_service.is_internal_path`) — not a client-side
  // conditional here — is what keeps Platform Admin/Workspace usage out
  // of the Website Visitors funnel (task spec §64), so this stays a
  // single unconditional call for every Surface. Fire-and-forget,
  // scheduled off the paint path; never awaited, never blocks render.
  useEffect(() => {
    initMarketingTracking()
    // Task spec §47/§48/§53 — GA4-READY, consent-gated (see `./analytics/
    // ga4.ts` / `./analytics/consent.ts`); a pure no-op today (no
    // Measurement ID configured, no Consent Manager granting `analytics`
    // yet) — never blocks/slows this render either way.
    initGa4IfConfigured()
  }, [])

  return (
    <ConfigProvider
      locale={antdLocale}
      theme={{
        token: {
          colorPrimary: '#5B5FF5',
          borderRadius: 6,
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Microsoft YaHei", "Noto Sans SC", sans-serif',
        },
      }}
      // ASSET-UX-CONSISTENCY-A1: every Modal/Dialog (including static
      // `Modal.confirm`/`Modal.warning`, which read this same ConfigProvider
      // context) must render truly centered in the current viewport, not
      // antd's default `top: 100px` anchor — one global override here covers
      // every call site at once instead of a per-instance `centered` prop
      // scattered across the codebase (see individual editor modals, which
      // additionally drop their old explicit `style.top` overrides so their
      // own large-but-fixed-size container is centered too, not just
      // standard-size dialogs).
      modal={{ centered: true }}
    >
      <PreviewAccessGate>
        {surface === 'marketing' ? <MarketingApp /> : surface === 'unified' ? <UnifiedLocalApp /> : <SaasAppShell />}
      </PreviewAccessGate>
    </ConfigProvider>
  )
}

export default function App() {
  // Phase SAAS-ENTRY-A1 — the ONE place Surface is decided (see
  // ./config/surface.ts). Marketing renders a completely separate tree
  // (own router, no AuthProvider, no Tenant/Media code reachable) —
  // everything below this branch for the App Surface is unchanged.
  const surface = resolveFrontendSurface()

  // VISTOERA_SAAS_I18N_FOUNDATION V1 — `LocaleProvider` mounts once, at the
  // absolute root, above `ConfigProvider` (spec §14: ONE locale source for
  // every Surface — Marketing, SaaS App, Platform Admin alike).
  return (
    <LocaleProvider>
      <LocalizedAppShell surface={surface} />
    </LocaleProvider>
  )
}
